/**
 * How the web decides whether the advid may be handed out.
 *
 * `@tv2/lib-consent` keeps that verdict on the consent manager
 * (`areAdvertisingIdentifiersAllowed()`), and leaves it to each CMP provider to record it — the
 * provider knows what its template actually contains. This module is that judgement for the web,
 * and it is the same rule Smart TV applies: advertising identifiers require FULL consent. Every
 * category in the Cookiebot template must be granted; any single category declined means no advid.
 *
 * Without full consent the persisted advid is deleted, not just withheld. A later consent then
 * starts from a fresh identifier with no link to the data collected under the old one.
 *
 * {@link connectAdvidConsent} keeps the manager's verdict in step with every recorded decision, so
 * a debug override that goes through the manager is judged exactly like a real answer.
 */
import { clearAdvid } from '@tv2/lib-consent/advid';
import {
  setAdvertisingIdentifiersAllowed,
  subscribeConsentDecision,
} from '@tv2/lib-consent/consentManager';
import { COOKIEBOT_EXPOSED_CATEGORIES } from '@tv2/lib-consent/consentDetails';
import { isConsentCategoryAllowed } from '@tv2/lib-consent/consentPolicy';

/**
 * Whether the advid may be handed out under the decision currently recorded: only when every
 * category Cookiebot asks about is granted (the IAB groups it cannot grant are not held against
 * it). Reads through the policy, so a missing decision counts as declined and a disabled
 * integration (no CMP configured) preserves pre-CMP behaviour.
 */
export function isFullConsentGiven(): boolean {
  return COOKIEBOT_EXPOSED_CATEGORIES.every(isConsentCategoryAllowed);
}

function recordAdvidVerdict(): void {
  const allowed = isFullConsentGiven();
  setAdvertisingIdentifiersAllowed(allowed);
  if (!allowed) clearAdvid();
}

/**
 * Starts keeping the manager's advertising-identifier verdict in step with the recorded consent,
 * deleting the advid whenever the verdict is no. Returns a function that stops doing so.
 *
 * Called by the Cookiebot provider when it starts, since recording the verdict is the provider's
 * job. Judges whatever is known right away, and again on every decision the manager is handed.
 */
export function connectAdvidConsent(): () => void {
  recordAdvidVerdict();
  return subscribeConsentDecision(recordAdvidVerdict);
}
